import React, { useRef, useEffect, useLayoutEffect, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { ProgressBar } from 'primereact/progressbar';
import { Toast } from 'primereact/toast';
import { logEvent } from '../../../../utils/analytics';
import { uploadImageToFirebase } from './firebaseImageUpload';
import { convertImageToWebp } from './imageToWebp';
import { logError } from '../../../../utils/analytics';
import './style.css';



export const ChatActions = ({
  isOwner,
  newMessage,
  setNewMessage,
  onSendMessage,
  isClosed,
  buzzMessage,
  selectedTag,
  isSending,
  shopperName,
  messages,
  userId,
  guestId
}) => {
  const toastRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  const isBlocked = selectedTag === "Block";

  // Image upload state
  const [imagePreviews, setImagePreviews] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]); // Store File objects until send
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Handle image file input
  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;
    const previews = files.map(file => URL.createObjectURL(file));
    setImagePreviews(prev => [...prev, ...previews]);
    setSelectedImages(prev => [...prev, ...files]);
    event.target.value = '';
  };

  // Trigger file input dialog
  const triggerImageSelect = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  // Clear input when shopperName changes (only when it actually changes)
  const prevShopperNameRef = useRef(shopperName);
  useLayoutEffect(() => {
    if (isOwner && prevShopperNameRef.current !== shopperName) {
      setNewMessage('');
      prevShopperNameRef.current = shopperName;
    }
  }, [shopperName, isOwner, setNewMessage]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    let messageStr = String(newMessage || '');
    const hasText = messageStr.replace(/<(.|\n)*?>/g, '').trim();
    const hasImages = selectedImages.length > 0;

    if (!hasText && !hasImages) return;

    try {
      setUploadingImages(true);
      setUploadProgress(0);
      let imageUrls = [];

      if (hasImages) {
        // Check if user is authenticated before uploading
        if (!userId) {
          throw new Error('You must be logged in to upload images');
        }

        const total = selectedImages.length;
        // Create a temporary array to track progress of each upload
        const progressArray = Array(total).fill(0);

        // Convert all images to webp before upload
        const webpFiles = await Promise.all(
          selectedImages.map(file => convertImageToWebp(file))
        );

        // Upload all files in parallel
        imageUrls = await Promise.all(
          webpFiles.map((file, idx) => {
            return uploadImageToFirebase(file, userId, guestId, (percent) => {
              // Update progress for this specific file
              progressArray[idx] = percent;
              // Calculate overall progress as average
              const sum = progressArray.reduce((a, b) => a + b, 0);
              const overallProgress = Math.round(sum / total);
              setUploadProgress(overallProgress);
            });
          })
        );


        // Wait for UI to show 100%
        setUploadProgress(100);
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      // Send the message with text and image URLs
      logEvent('sendMessage', { messageLength: messageStr.length, imageCount: imageUrls.length });
      onSendMessage({ text: messageStr, images: imageUrls });

      // Reset form state
      setNewMessage('');
      setImagePreviews([]);
      setSelectedImages([]);
      // Remove focus from input after sending
      if (inputRef.current) {
        inputRef.current.blur();
      }

    } catch (err) {
      logError('ChatActions: image upload failed', err);
      // Check for specific Firebase storage errors
      let errorMessage = err.message || 'Failed to upload image';

      if (err.code === 'storage/unauthorized') {
        errorMessage = 'Permission denied: You do not have access to upload images';
      } else if (err.code === 'storage/canceled') {
        errorMessage = 'Upload was canceled';
      } else if (err.code === 'storage/retry-limit-exceeded') {
        errorMessage = 'Upload failed: Network error, please try again';
      }

      if (toastRef.current) {
        toastRef.current.show({
          severity: 'error',
          summary: 'Upload Failed',
          detail: errorMessage,
          life: 5000 // Show for 5 seconds
        });
      }
    } finally {
      setUploadingImages(false);
      setUploadProgress(0);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <div className="chat-actions">
      <Toast ref={toastRef} />

      {uploadingImages && (
        <div style={{ marginBottom: 12 }}>
          <ProgressBar
            value={uploadProgress}
            showValue={true}
            unit="%"
            style={{ height: '8px' }}
          />
        </div>
      )}

      {imagePreviews.length > 0 && (
        <div className="image-preview-container" style={{ marginBottom: 8, display: 'flex', flexWrap: 'wrap', gap: 12 }}>
          {imagePreviews.map((src, idx) => (
            <div key={src + idx} style={{ position: 'relative', display: 'inline-block' }}>
              <img
                src={src}
                alt={`preview-${idx}`}
                style={{
                  maxWidth: 120,
                  maxHeight: 120,
                  borderRadius: 8,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  objectFit: 'cover'
                }}
              />
              <Button
                icon="pi pi-times"
                className="p-button-rounded p-button-danger p-button-text p-button-sm"
                style={{
                  position: 'absolute',
                  top: -5,
                  right: -5,
                  zIndex: 2,
                  width: '1.5rem',
                  height: '1.5rem'
                }}
                onClick={() => {
                  setImagePreviews(prev => prev.filter((_, i) => i !== idx));
                  setSelectedImages(prev => prev.filter((_, i) => i !== idx));
                }}
                tooltip="Remove image"
                tooltipOptions={{ position: 'top' }}
              />
            </div>
          ))}
        </div>
      )}
      <form onSubmit={handleSubmit} className="p-inputgroup">
        <InputText
          ref={inputRef}
          value={String(newMessage || "")}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={isBlocked ? "You cannot send messages to this shop" : isClosed ? "Chat is closed" : "Type a message"}
          disabled={isClosed || isBlocked || isSending || uploadingImages}
          autoFocus
        />
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: 'none' }}
          onChange={handleImageUpload}
        />
        <Button
          type="button"
          icon="pi pi-image"
          className="p-button p-button-primary"
          onClick={triggerImageSelect}
          tooltip="Attach Image"
          tooltipOptions={{ position: 'top' }}
          disabled={isClosed || isBlocked || isSending || uploadingImages}
        />
        <Button
          type="submit"
          className="p-button p-button-primary"
          icon={isSending || uploadingImages ? "pi pi-spin pi-spinner" : "pi pi-send"}
          disabled={
            isClosed ||
            (!newMessage.trim() && imagePreviews.length === 0) ||
            isBlocked ||
            isSending ||
            uploadingImages
          }
          style={{ minWidth: 40 }}
        />

        {/* {showBuzzButton && (
          <Button
            type="button"
            icon="pi pi-bell"
            onClick={buzzMessage}
            disabled={isClosed || isBlocked || isSending}
            className="buzz-button p-button p-button-primary"
          />
        )} */}
      </form>

    </div>
  );
}; 